import { Component, ElementRef, Input, OnChanges, signal, ViewChild, WritableSignal } from '@angular/core';
import { ComponentChanges } from '@app/utils/simpleChangesType';
import { Block, BlockButton, BlockType, ButtonType, StepData } from '@shared/models/step.model';
import { ButtonPadding, ButtonVariant } from '@app/components/button/button.component';
import { PlIcon } from '@app/icon-library/icons';
import { PositionX, PositionY } from '@app/components/pl-popover/pl-popover.component';
import { PdfGeneratorService } from '@shared/services/pdfGenerator/pdfGenerator.service';
import { opacityAnimation } from '@app/utils/animations';

interface MenuItem {
  label: string | null;
  link: string;
  type: string;
}

@Component({
  selector: 'pl-my-life-acts-download-button',
  templateUrl: './myLifeActsDownloadButton.component.html',
  animations: [opacityAnimation],
})
export class MyLifeActsDownloadButtonComponent implements OnChanges {
  @Input({ required: true }) public lifeactStepData!: StepData | null;

  public menuItems: WritableSignal<MenuItem[]> = signal([]);
  public pdfGeneratingInProgress: WritableSignal<boolean> = signal(false);
  public menuItemButtons: WritableSignal<BlockButton[]> = signal([]);

  protected readonly BlockType: typeof BlockType = BlockType;
  protected readonly ButtonPadding: typeof ButtonPadding = ButtonPadding;
  protected readonly ButtonVariant: typeof ButtonVariant = ButtonVariant;
  protected readonly PlIcon: typeof PlIcon = PlIcon;
  protected readonly PositionX: typeof PositionX = PositionX;
  protected readonly PositionY: typeof PositionY = PositionY;

  @ViewChild('buttonToLinkPopover') public buttonToLinkPopover!: ElementRef;
  public showMenu: WritableSignal<boolean> = signal(false);

  constructor(private pdfGeneratorService: PdfGeneratorService) {}

  public ngOnChanges(changes: ComponentChanges<MyLifeActsDownloadButtonComponent>): void {
    if (changes.lifeactStepData) {
      const lifeactStepData: StepData | null = changes.lifeactStepData.currentValue;
      if (lifeactStepData) {
        this.menuItemButtons.set(
          lifeactStepData.blocks.filter(
            (value: Block | null) => value && value.element === BlockType.button,
          ) as BlockButton[],
        );
        const blockButtons: BlockButton[] = lifeactStepData.blocks.filter(
          (value: Block | null) => value && value.element === BlockType.button,
        ) as BlockButton[];
        this.menuItemButtons.set(blockButtons);

        this.menuItems.set(
          blockButtons.map((blockButton: BlockButton): MenuItem => {
            return {
              label: blockButton.data.label,
              link: blockButton.data.href as string,
              type: blockButton.data.type,
            };
          }),
        );
      } else {
        this.menuItems.set([]);
      }
    }
  }

  public closeMenu(): void {
    this.showMenu.set(false);
  }

  public openMenu(): void {
    this.showMenu.set(true);
  }

  public async onMenuItemClicked(item: MenuItem): Promise<void> {
    if (this.pdfGeneratingInProgress()) {
      return;
    }

    switch (item.type) {
      case ButtonType.link:
        window.open(item.link, '_blank');
        break;
      case ButtonType.pdf:
        this.pdfGeneratingInProgress.set(true);
        await this.pdfGeneratorService.generateAndDownloadPdf(item.link);
        this.pdfGeneratingInProgress.set(false);
        break;
      default:
        throw new Error(`Unknown buttonType: ${item.type}`);
    }
    this.closeMenu();
  }
}
